import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Ico } from './icons';
import { Page } from './page';
import { parseTags } from '../lib/tags';
import { API } from '../lib/api';
import { exportPdf } from '../lib/export_pdf';
import { readFromUrl, writeToUrl, toApiParams, apiParamsToSearchString } from '../lib/community_query';
import { SmartSearch } from './smart_search';

const fmtCount = (n) => n >= 1000 ? (n/1000).toFixed(1).replace(/\.0$/,'') + 'k' : String(n);

function TagChips({ tags, className = 'cm-card-tags' }) {
  const ref = useRef(null);
  const widths = useRef([]);
  const gapRef = useRef(4);
  const [limit, setLimit] = useState(tags ? tags.length : 0);

  const measure = () => {
    const el = ref.current;
    if (!el || !tags || !tags.length) return;
    const chips = Array.from(el.querySelectorAll('[data-tag]'));
    if (chips.length === tags.length) {
      widths.current = chips.map(c => c.offsetWidth);
    }
    gapRef.current = parseFloat(getComputedStyle(el).gap) || 4;
  };

  const fit = () => {
    const el = ref.current;
    if (!el || !tags || !tags.length) return;
    const w = widths.current;
    const gap = gapRef.current;
    const cw = el.offsetWidth;
    if (!w.length) { setLimit(tags.length); return; }
    const total = w.reduce((s, v, i) => s + v + (i > 0 ? gap : 0), 0);
    if (total <= cw) { setLimit(tags.length); return; }
    const counterW = 28 + gap;
    let used = 0;
    let count = 0;
    for (let i = 0; i < w.length; i++) {
      const next = used + w[i] + (i > 0 ? gap : 0);
      if (next + counterW <= cw) { used = next; count++; }
      else break;
    }
    setLimit(Math.max(count, 1));
  };

  useLayoutEffect(() => {
    measure();
    fit();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [tags]);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts?.ready) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled) return;
      measure();
      fit();
    });
    return () => { cancelled = true; };
  }, [tags]);

  if (!tags || tags.length === 0) {
    return (
      <div className={className}>
        <span className="chip chip--tag" style={{ visibility: 'hidden' }}>&nbsp;</span>
      </div>
    );
  }

  const hidden = tags.length - limit;
  return (
    <div className={className} ref={ref}>
      {tags.slice(0, limit).map((tag, i) => (
        <span key={i} className="chip chip--tag" data-tag>{tag}</span>
      ))}
      {hidden > 0 && <span className="chip chip--muted">+{hidden}</span>}
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff/60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)} hr ago`;
  if (diff < 604800) return `${Math.floor(diff/86400)} day${Math.floor(diff/86400)!==1?'s':''} ago`;
  const weeks = Math.floor(diff / 604800);
  if (weeks < 5) return `${weeks} wk ago`;
  const months = Math.floor(diff / 2592000);
  if (months < 12) return `${months} month${months!==1?'s':''} ago`;
  const years = Math.floor(months / 12);
  return `over ${years} year${years!==1?'s':''} ago`;
}

function apiDocToCard(doc) {
  const tabs = doc.content?.tabs || [];
  const bySide = {};
  tabs.forEach(t => {
    const side = t.location || 'left';
    if (!bySide[side]) bySide[side] = [];
    bySide[side].push({ label: t.label, color: t.color });
  });
  return {
    id: doc.id,
    toParam: doc.to_param,
    name: doc.name,
    description: doc.description || '',
    tags: parseTags(doc.tags_string),
    editPath: doc.edit_path,
    clonePath: doc.clone_path,
    deletePath: doc.delete_path,
    favoritePath: doc.favorite_path,
    previewImageUrl: doc.preview_image_url || null,
    exportable: !!doc.exportable,
    exports: doc.exports_count || 0,
    forks: doc.forks_count || 0,
    favorites: doc.favorites_count || 0,
    favorited: !!doc.favorited,
    canFavorite: !!doc.can_favorite,
    tabs: tabs.length,
    updated: timeAgo(doc.updated_at),
    updatedAt: doc.updated_at,
    content: doc.content,
    cover: {
      orientation: doc.content?.orientation || 'portrait',
      rails: Object.entries(bySide).map(([side, tabs]) => ({ side, tabs }))
    }
  };
}

function StarIcon({ filled }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16"
         fill={filled ? 'currentColor' : 'none'}
         stroke="currentColor" strokeWidth="1.3">
      <path d="M8 1.5l2 4.2 4.5.7-3.3 3.2.8 4.5L8 11.9l-4 2.2.8-4.5L1.5 6.4l4.5-.7L8 1.5z"/>
    </svg>
  );
}

export function StarButton({ favorited, count, onClick, className = '', showCount = false, disabled = false }) {
  return (
    <button type="button"
            className={`dc-star ${favorited ? 'on' : ''} ${className}`}
            disabled={disabled}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) onClick && onClick(); }}
            title={favorited ? 'Unstar' : 'Star'}
            aria-pressed={favorited}>
      <StarIcon filled={favorited}/>
      {showCount && <span className="ty-body-sm">{fmtCount(count || 0)}</span>}
    </button>
  );
}

function toggleFavoriteOnServer(favoritePath, nextFavorited) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
  return fetch(favoritePath, {
    method: nextFavorited ? 'POST' : 'DELETE',
    headers: { 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) }
  }).then(r => {
    if (!r.ok) throw new Error('Failed to update favorite');
    return r.json();
  });
}

const AVATAR_COLORS = ['blue', 'teal', 'green', 'purple', 'orange', 'red', 'yellow', 'gray'];
function colorForUsername(username) {
  let h = 0;
  for (let i = 0; i < username.length; i++) h = (h * 31 + username.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function apiDocToCommunityCard(doc) {
  const card = apiDocToCard(doc);
  const username = doc.author?.username || 'pilot';
  return {
    ...card,
    previewUrl: `/documents/${card.toParam || card.id}`,
    author: {
      name: username,
      verified: false,
      avatar: username.slice(0, 2).toUpperCase(),
      avatarColor: colorForUsername(username)
    },
    clones: card.forks
  };
}

function buildPreviewDoc(cdoc) {
  const content = cdoc.content || {};
  return {
    name: cdoc.name,
    description: cdoc.description,
    orientation: content.orientation || cdoc.cover?.orientation || 'portrait',
    tabs: content.tabs || [],
    pages: content.pages || {},
    railSizes: content.railSizes || {}
  };
}

export function DocPreviewScreen({ doc, isLoggedIn, onToast, documentsCount, maxDocuments }) {
  const previewDoc = useMemo(() => buildPreviewDoc(doc), [doc]);
  const visibleTabs = previewDoc.tabs.filter(t => t.label);
  const firstTab = visibleTabs[0];
  const [activePageId, setActivePageId] = useState(firstTab?.pageIds[0]);
  const activePage = previewDoc.pages[activePageId] || Object.values(previewDoc.pages)[0];
  const previewRef = useRef(null);
  const [scale, setScale] = useState(0.6);
  const [limitModal, setLimitModal] = useState(false);
  const [favorited, setFavorited] = useState(doc.favorited);
  const [favoritesCount, setFavoritesCount] = useState(doc.favorites);
  const [favoritePending, setFavoritePending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cloning, setCloning] = useState(false);
  const gateRef = useRef(null);

  // Back-to-list behavior:
  //   - If the user clicked through from /library in this tab, we saved the
  //     exact URL to sessionStorage. Use it as the href.
  //   - Else, fall back to document.referrer (covers new-tab opens where
  //     sessionStorage is fresh but referrer is preserved).
  //   - Else, navigate to /library.
  //
  // The click handler prefers history.back() when this tab has somewhere to
  // pop back to, so the previous page is restored from bfcache (filter state,
  // scroll position, and loaded pages intact). New tabs (history.length === 1)
  // fall through to the href.
  const backHref = useMemo(() => {
    try {
      const saved = sessionStorage.getItem(RETURN_TO_LIST_KEY);
      if (saved) return saved;
    } catch {}
    try {
      const ref = new URL(document.referrer || '');
      if (ref.origin === window.location.origin && ref.pathname === '/library') {
        return ref.pathname + ref.search + ref.hash;
      }
    } catch {}
    return '/library';
  }, []);

  const handleBackClick = (e) => {
    // Only history.back() when we have positive evidence the previous history
    // entry is in-app. Otherwise (external referrer like Google, new tab) let
    // the <a href={backHref}> default fire — backHref already resolves to a
    // sensible in-app destination.
    let inApp = false;
    try { inApp = !!sessionStorage.getItem(RETURN_TO_LIST_KEY); } catch {}
    if (!inApp) {
      try {
        const ref = new URL(document.referrer || '');
        inApp = ref.origin === window.location.origin;
      } catch {}
    }
    if (inApp && window.history.length > 1) {
      e.preventDefault();
      window.history.back();
    }
  };

  const handleToggleFavorite = () => {
    if (favoritePending) return;
    const wasFavorited = favorited;
    const wasFavoritesCount = favoritesCount;
    const next = !wasFavorited;
    setFavorited(next);
    setFavoritesCount(c => Math.max(0, c + (next ? 1 : -1)));
    setFavoritePending(true);
    toggleFavoriteOnServer(doc.favoritePath, next)
      .then(data => { setFavorited(data.favorited); setFavoritesCount(data.favorites_count); })
      .catch(() => {
        setFavorited(wasFavorited);
        setFavoritesCount(wasFavoritesCount);
        onToast && onToast('Failed to update favorite', { type: 'error' });
      })
      .finally(() => setFavoritePending(false));
  };

  const handleGuestStar = () => {
    if (gateRef.current) {
      gateRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      const pw = previewDoc.orientation === 'landscape' ? 1123 : 794;
      const ph = previewDoc.orientation === 'landscape' ? 794 : 1123;
      const pad = isLoggedIn ? 48 : 16;
      const sx = (width - pad) / pw;
      const sy = (height - pad) / ph;
      setScale(Math.max(0.1, Math.min(sx, sy, 1)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [previewDoc, isLoggedIn]);

  const handleTabClick = (tabId) => {
    if (!isLoggedIn) return;
    const t = previewDoc.tabs.find(x => x.id === tabId);
    if (t && t.pageIds[0]) setActivePageId(t.pageIds[0]);
  };

  const handleClone = () => {
    if (cloning) return;
    if (maxDocuments != null && documentsCount >= maxDocuments) {
      setLimitModal(true);
      return;
    }
    setCloning(true);
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(API.cloneDocument(doc.id), {
      method: 'POST',
      headers: { 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) }
    })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok) { window.track && window.track('document_cloned', { source: 'community' }); window.location.href = data.edit_path; }
        else { onToast && onToast(data.errors?.[0] || data.error || 'Failed to duplicate document', { type: 'error' }); setCloning(false); }
      })
      .catch(err => {
        console.error('duplicate document failed:', err);
        onToast && onToast('Failed to duplicate document', { type: 'error' });
        setCloning(false);
      });
  };

  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    exportPdf({ id: doc.id, name: doc.name }, { onToast })
      .then(() => window.track && window.track('pdf_exported', { source: 'community' }))
      .finally(() => setExporting(false));
  };

  if (!isLoggedIn) {
    return (
      <div className="cpv cpv--guest">
        <div className="cpv-guest-hero">
          <a className="cpv-back" href={backHref} onClick={handleBackClick}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to library
          </a>
          <h1 className="cpv-guest-title ty-heading-lg">{doc.name}</h1>
          <div className="cpv-guest-stats ty-body-sm">
            <a className="cpv-author" href={`/library?author=${encodeURIComponent(doc.author.name)}`}>
              <div className={`avatar avatar--sm avatar--${doc.author.avatarColor}`}>{doc.author.avatar}</div>
              <span>{doc.author.name}</span>
            </a>
            <span className="cm-stat-sep">&middot;</span>
            <span title="Exports" className="cm-stat-cell"><Ico.Download width="14" height="14"/> {fmtCount(doc.exports || 0)} exports</span>
            <span className="cm-stat-sep">&middot;</span>
            <span title="Duplicates" className="cm-stat-cell"><Ico.Copy width="14" height="14"/> {fmtCount(doc.clones || 0)} duplicates</span>
            <span className="cm-stat-sep">&middot;</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleGuestStar} title="Favorites">
              <StarIcon filled={false}/> {fmtCount(favoritesCount)}
            </button>
          </div>
        </div>
        <div className="cpv-preview cpv-preview--guest" ref={previewRef}>
          <div className="cpv-page-wrap cpv-locked" style={{ zoom: scale }}>
            {activePage && (
              <Page doc={previewDoc}
                    page={previewDoc.pages[firstTab?.pageIds[0]] || activePage}
                    currentTabId={firstTab?.id}
                    onTabClick={handleTabClick}
                    onTabDoubleClick={() => {}}
                    onTabDrop={() => {}}
                    dragTabId={null}
                    setDragTabId={() => {}}
                    isEditing={false}
                    onEditContent={() => {}}
                    onEditContent2={() => {}}/>
            )}
          </div>
          <div className="cpv-gate" ref={gateRef}>
            <div className="cpv-gate-fade"/>
            <div className="cpv-gate-bar">
              <div className="cpv-gate-content">
                <div className="cpv-gate-msg">
                  <Ico.Aircraft width="16" height="16"/>
                  <span className="ty-body-md">Create an account to export, duplicate, and build your own checklists.</span>
                </div>
                <a className="btn btn--primary btn--md"
                   href="/create-account"
                   onClick={() => window.track && window.track('create_account_clicked', { location: 'community_preview' })}>
                  Create account
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cpv">
      <div className="cpv-body">
        <aside className="cpv-sidebar">
          <div className="cpv-sidebar-top">
            <a className="cpv-back" href={backHref} onClick={handleBackClick}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to library
            </a>
          </div>
          <div className="cpv-sidebar-main">
            <h1 className="cpv-title ty-heading-lg">{doc.name}</h1>
            <p className="cpv-desc ty-body-md">{doc.description}</p>
            <div className="cpv-meta ty-body-sm">
              <a className="cpv-author" href={`/library?author=${encodeURIComponent(doc.author.name)}`}>
                <div className={`avatar avatar--sm avatar--${doc.author.avatarColor}`}>{doc.author.avatar}</div>
                <span>{doc.author.name}</span>
              </a>
              <span className="cm-stat-sep">&middot;</span>
              <span title="Exports" className="cm-stat-cell"><Ico.Download width="12" height="12"/> {fmtCount(doc.exports || 0)}</span>
              <span className="cm-stat-sep">&middot;</span>
              <span title="Duplicates" className="cm-stat-cell"><Ico.Copy width="12" height="12"/> {fmtCount(doc.clones || 0)}</span>
              {doc.canFavorite && (
                <>
                  <span className="cm-stat-sep">&middot;</span>
                  <span title="Favorites" className="cm-stat-cell"><StarIcon filled={favorited}/> {fmtCount(favoritesCount)}</span>
                </>
              )}
            </div>
            <div className="cpv-actions">
              <button className="btn btn--primary btn--md cpv-act" onClick={handleExport} disabled={exporting}>
                <Ico.Download width="14" height="14"/> {exporting ? 'Exporting...' : 'Export PDF'}
              </button>
              <button className="btn btn--ghost btn--md btn--square" onClick={handleClone} disabled={cloning} title="Duplicate to edit">
                <Ico.Copy width="16" height="16"/>
              </button>
              {doc.canFavorite && (
                <button type="button"
                        className="btn btn--ghost btn--md btn--square"
                        onClick={handleToggleFavorite}
                        disabled={favoritePending}
                        aria-pressed={favorited}
                        title={favorited ? 'Unstar' : 'Star'}>
                  <StarIcon filled={favorited}/>
                </button>
              )}
            </div>
          </div>
          <div className="cpv-sidebar-bottom">
            <div className="cpv-tags">
              {doc.tags && doc.tags.map((tag, i) => (
                <span key={i} className="chip chip--tag">{tag}</span>
              ))}
            </div>
            <div className="cpv-bottom-stats ty-body-sm">
              Updated {doc.updated}
            </div>
          </div>
        </aside>
        <div className="cpv-preview" ref={previewRef}>
          <div className="cpv-page-wrap" style={{ zoom: scale }}>
            {activePage && (
              <Page doc={previewDoc}
                    page={activePage}
                    currentTabId={activePage.tabId}
                    onTabClick={handleTabClick}
                    onTabDoubleClick={() => {}}
                    onTabDrop={() => {}}
                    dragTabId={null}
                    setDragTabId={() => {}}
                    isEditing={false}
                    onEditContent={() => {}}
                    onEditContent2={() => {}}/>
            )}
          </div>
        </div>
      </div>
      {limitModal && (
        <div className="modal-backdrop" onClick={() => setLimitModal(false)}>
          <div className="modal modal--sm" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Document limit reached</h2>
              <button className="btn btn--icon" aria-label="Close" onClick={() => setLimitModal(false)}><Ico.Close width="16" height="16"/></button>
            </div>
            <div className="modal__body">
              <p>You've used all {maxDocuments} of your document slots. Delete an existing document to make room.</p>
            </div>
            <div className="modal__footer">
              <button className="btn btn--primary btn--md" onClick={() => setLimitModal(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export function CommunityCover({ cover, name, previewImageUrl }) {
  const orient = cover.orientation || 'portrait';
  const [imgFailed, setImgFailed] = useState(false);
  if (previewImageUrl && !imgFailed) {
    return (
      <div className={`dc-cover dc-${orient}`}>
        <img
          src={previewImageUrl}
          loading="lazy"
          alt={name}
          className="dc-preview-img"
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }
  const bySide = { top:[], bottom:[], left:[], right:[] };
  (cover.rails || []).forEach(r => { bySide[r.side] = r.tabs || []; });
  const renderRail = (side) => {
    const tabs = bySide[side];
    if (!tabs.length) return null;
    return (
      <div className={`dc-rail dc-rail-${side}`}>
        {tabs.map((t,i)=>(
          <div key={i} className={`dc-chip dc-c-${t.color}`}>{t.label}</div>
        ))}
      </div>
    );
  };
  return (
    <div className={`dc-cover dc-${orient}`}>
      {renderRail('top')}
      <div className="dc-mid">
        {renderRail('left')}
        <div className="dc-content">
          <div className="dc-title-line">{name}</div>
          <div className="dc-line"/><div className="dc-line short"/><div className="dc-line"/>
          <div className="dc-line"/><div className="dc-line mid"/><div className="dc-line"/>
        </div>
        {renderRail('right')}
      </div>
      {renderRail('bottom')}
    </div>
  );
}


function MyDocCard({ doc, exporting, onExport, onDelete, onToast }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="card cm-card">
      <div className="card__cover cm-card-cover">
        <CommunityCover cover={doc.cover} name={doc.name} previewImageUrl={doc.previewImageUrl}/>
        {!doc.exportable && (
          <span className="cm-card-status cm-card-status--private">
            <Ico.Eye width="10" height="10"/> Private
          </span>
        )}
      </div>
      <div className="cm-card-body">
        <div className="cm-card-name-row">
          <a className="cm-card-name cm-card-name-link ty-heading-xs" href={doc.editPath} data-turbo-prefetch="false">{doc.name}</a>
          <div className="cm-card-menu-wrap">
            <button className="cm-card-menu-btn"
                    aria-label="Document actions"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={e=>{e.preventDefault(); e.stopPropagation(); setMenuOpen(o=>!o);}}>
              <Ico.More width="14" height="14"/>
            </button>
            {menuOpen && (
              <>
                <div className="dc-menu-scrim" onClick={e=>{e.preventDefault(); e.stopPropagation(); setMenuOpen(false);}}/>
                <div className="menu cm-card-menu" onClick={e=>e.stopPropagation()}>
                  <button className="menu__item" disabled={exporting}
                    onClick={e=>{e.preventDefault(); setMenuOpen(false); onExport && onExport(doc);}}>
                    <Ico.Download width="12" height="12"/> {exporting ? 'Exporting…' : 'Export PDF'}
                  </button>
                  <button className="menu__item" onClick={e=>{
                    e.preventDefault();
                    setMenuOpen(false);
                    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
                    fetch(doc.clonePath, {
                      method: 'POST',
                      headers: { 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) }
                    })
                      .then(r => r.json().then(data => ({ ok: r.ok, data })))
                      .then(({ ok, data }) => {
                        if (ok) { window.track && window.track('document_cloned', { source: 'library' }); window.location.href = data.edit_path; }
                        else { onToast && onToast(data.errors?.[0] || 'Failed to duplicate document', { type: 'error' }); }
                      })
                      .catch(err => {
                        console.error('duplicate document failed:', err);
                        onToast && onToast('Failed to duplicate document', { type: 'error' });
                      });
                  }}><Ico.Copy width="12" height="12"/> Duplicate</button>
                  <div className="menu__separator"/>
                  <button className="menu__item menu__item--danger" onClick={e=>{e.preventDefault(); setMenuOpen(false); onDelete && onDelete(doc);}}><Ico.Trash width="12" height="12"/> Delete</button>
                </div>
              </>
            )}
          </div>
        </div>
        <TagChips tags={doc.tags}/>
        <div className="cm-card-stats ty-body-sm">
          <span title={doc.updatedAt ? new Date(doc.updatedAt).toLocaleString() : ''}>{doc.updated}</span>
          {doc.exportable && (
            <div className="cm-stat-group">
              <span title="Exports" className="cm-stat-cell"><Ico.Download width="12" height="12"/> {fmtCount(doc.exports)}</span>
              <span className="cm-stat-sep">·</span>
              <span title="Duplicates" className="cm-stat-cell"><Ico.Copy width="12" height="12"/> {fmtCount(doc.forks)}</span>
              <span className="cm-stat-sep">·</span>
              <span title="Favorites" className="cm-stat-cell"><StarIcon filled={false}/> {fmtCount(doc.favorites || 0)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const RETURN_TO_LIST_KEY = 'community-return-url';

function rememberReturnUrl() {
  try {
    sessionStorage.setItem(
      RETURN_TO_LIST_KEY,
      window.location.pathname + window.location.search + window.location.hash
    );
  } catch {}
}

function CommunityCard({ doc, onToggleFavorite, onGuestStar, favoritePending = false }) {
  const showStar = doc.canFavorite || onGuestStar;
  const handleStarClick = () => {
    if (onGuestStar) { onGuestStar(); return; }
    onToggleFavorite && onToggleFavorite(doc);
  };
  return (
    <div className="card cm-card">
      <div className="card__cover cm-card-cover">
        <CommunityCover cover={doc.cover} name={doc.name} previewImageUrl={doc.previewImageUrl}/>
        {showStar && (
          <StarButton className="cm-card-star"
                      favorited={doc.favorited}
                      onClick={handleStarClick}
                      disabled={favoritePending}/>
        )}
      </div>
      <div className="cm-card-body">
        <a className="cm-card-name cm-card-name-link ty-heading-xs" href={doc.previewUrl} data-turbo-prefetch="false" onClick={() => { window.track && window.track('community_doc_previewed'); rememberReturnUrl(); }}>{doc.name}</a>
        <div className="cm-card-author">
          <div className={`avatar avatar--sm avatar--${doc.author.avatarColor}`}>{doc.author.avatar}</div>
          <span className="cm-card-aname ty-body-sm">{doc.author.name}</span>
        </div>
        <TagChips tags={doc.tags}/>
        <div className="cm-card-stats ty-body-sm">
          <span title={doc.updatedAt ? new Date(doc.updatedAt).toLocaleString() : ''}>{doc.updated}</span>
          <div className="cm-stat-group">
            <span title="Exports" className="cm-stat-cell"><Ico.Download width="12" height="12"/> {fmtCount(doc.exports || 0)}</span>
            <span className="cm-stat-sep">·</span>
            <span title="Duplicates" className="cm-stat-cell"><Ico.Copy width="12" height="12"/> {fmtCount(doc.clones || 0)}</span>
            <span className="cm-stat-sep">·</span>
            <span title="Favorites" className="cm-stat-cell"><StarIcon filled={doc.favorited}/> {fmtCount(doc.favorites || 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MyDocCardSkeleton() {
  return (
    <div className="card cm-card cm-card-skeleton" aria-hidden="true">
      <div className="card__cover cm-card-cover">
        <div className="cm-skel-cover"/>
      </div>
      <div className="cm-card-body">
        <div className="cm-skel-line cm-skel-line--title"/>
        <div className="cm-skel-line cm-skel-line--meta"/>
      </div>
    </div>
  );
}

export function MyDocumentsSection({ library: initialLibrary, maxDocuments, loading, creating, onNewDoc, onToast }) {
  const [library, setLibrary] = useState(() => (initialLibrary || []).map(apiDocToCard));
  useEffect(() => { if (initialLibrary) setLibrary(initialLibrary.map(apiDocToCard)); }, [initialLibrary]);
  const atLimit = library.length >= maxDocuments;
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionConfirm, setActionConfirm] = useState(null);
  const [exportingIds, setExportingIds] = useState(() => new Set());

  const handleExport = (doc) => {
    if (exportingIds.has(doc.id)) return;
    if (doc.exportable) {
      setExportingIds(s => { const n = new Set(s); n.add(doc.id); return n; });
      exportPdf({ id: doc.id, name: doc.name }, { onToast })
        .then(() => window.track && window.track('pdf_exported', { source: 'library' }))
        .finally(() => setExportingIds(s => { const n = new Set(s); n.delete(doc.id); return n; }));
    } else {
      setActionConfirm(doc);
    }
  };
  const confirmPublishAndExport = () => {
    if (!actionConfirm) return;
    const doc = actionConfirm;
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(API.document(doc.id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) },
      body: JSON.stringify({ document: { visibility: 'public' } })
    })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(() => {
        window.track && window.track('document_published', { source: 'library' });
        setLibrary(lib => lib.map(d => d.id === doc.id ? { ...d, exportable: true } : d));
        window.track && window.track('pdf_exported', { source: 'library' });
        exportPdf({ id: doc.id, name: doc.name }, { onToast });
      })
      .catch(() => onToast && onToast('Failed to publish document', { type: 'error' }));
    setActionConfirm(null);
  };

  const confirmDelete = (doc) => {
    setDeleteConfirm(null);
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(doc.deletePath, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json', ...(csrf && { 'X-CSRF-Token': csrf }) }
    })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          window.track && window.track('document_deleted', { source: 'library' });
          setLibrary(lib => lib.filter(d => d.id !== doc.id));
          onToast && onToast(`"${doc.name}" deleted`, { type: 'info' });
        } else {
          onToast && onToast(data.errors?.[0] || 'Failed to delete document', { type: 'error' });
        }
      })
      .catch(err => {
        console.error('delete document failed:', err);
        onToast && onToast('Failed to delete document', { type: 'error' });
      });
  };

  const handleNewDoc = () => {
    if (atLimit) {
      onToast && onToast(`Document limit reached (${maxDocuments} max). Delete a document to make room.`, { type: 'warning' });
      return;
    }
    onNewDoc && onNewDoc();
  };

  return (
    <div className="slot-section">
      <div className="cm-grid">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <MyDocCardSkeleton key={`skel-${i}`}/>)
        ) : (
          <>
            {!atLimit && (
              <button key="new" className={`slot-new${creating ? ' slot-new--creating' : ''}`} onClick={handleNewDoc} disabled={creating}>
                <div className={`slot-new-icon${creating ? ' slot-new-icon--spin' : ''}`}><Ico.Plus width="18" height="18"/></div>
                <div className="slot-new-label ty-heading-compact-01">{creating ? 'Creating...' : 'Create new document'}</div>
                <div className="slot-new-sub ty-body-sm">
                  {maxDocuments - library.length} slot{maxDocuments - library.length !== 1 ? 's' : ''} remaining
                </div>
              </button>
            )}
            {library.map(d => (
              <MyDocCard key={d.id} doc={d}
                exporting={exportingIds.has(d.id)}
                onExport={handleExport}
                onDelete={setDeleteConfirm}
                onToast={onToast}/>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal--sm" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Delete "{deleteConfirm.name}"?</h2>
              <button className="btn btn--icon" onClick={() => setDeleteConfirm(null)}><Ico.Close width="16" height="16"/></button>
            </div>
            <div className="modal__body">
              <p className="confirm-public">This document will be permanently deleted. This action cannot be undone.</p>
            </div>
            <div className="modal__footer">
              <button className="btn btn--ghost btn--md" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn--danger btn--md" onClick={() => confirmDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {actionConfirm && (
        <div className="modal-backdrop" onClick={() => setActionConfirm(null)}>
          <div className="modal modal--sm" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Export "{actionConfirm.name}"?</h2>
              <button className="btn btn--icon" aria-label="Close" onClick={() => setActionConfirm(null)}><Ico.Close width="16" height="16"/></button>
            </div>
            <div className="modal__body">
              <p>Exporting will also publish this document to the community, making it visible to all pilots. Future saves will update the published version.</p>
            </div>
            <div className="modal__footer">
              <button className="btn btn--ghost btn--md" onClick={() => setActionConfirm(null)}>Cancel</button>
              <button className="btn btn--primary btn--md" onClick={confirmPublishAndExport}><Ico.Download width="14" height="14"/> Publish & export</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CommunitySection({ isLoggedIn, onToast }) {
  const [state, setState] = useState(() => readFromUrl(location.search));
  const [pages, setPages] = useState([]);                  // [[doc, doc...], [doc...]]
  const [firstLoadedPage, setFirstLoadedPage] = useState(state.page);
  const [pagination, setPagination] = useState(null);      // {page, pages, count, next, prev}
  const [loadingDir, setLoadingDir] = useState('initial'); // 'initial' | 'next' | 'prev' | null
  const [pendingFavoriteIds, setPendingFavoriteIds] = useState(() => new Set());
  const sentinelRef = useRef(null);
  const pageMarkersRef = useRef(new Map());
  const abortRef = useRef(null);
  const isInitialMount = useRef(true);
  // Bypass the fetch-effect debounce for discrete-click changes (sort, tab).
  // Typing and tag toggles still debounce so we don't hammer the backend.
  const skipDebounceRef = useRef(false);
  // Set by the popstate handler so the next fetch honors the URL's page param
  // instead of resetting to page 1 (which is the right behavior for filter
  // changes, but wrong when restoring a back/forward history entry).
  const restoreFromUrlRef = useRef(false);

  // Guests can't have favorites; coerce.
  const effectiveState = useMemo(() => (
    isLoggedIn ? state : { ...state, tab: 'explore' }
  ), [state, isLoggedIn]);

  // Low-level: fetch one page using a caller-provided AbortSignal.
  const fetchOne = (st, page, signal) => {
    const search = apiParamsToSearchString(toApiParams({ ...st, page }));
    return fetch(API.documentsPublic(search), {
      headers: { 'Accept': 'application/json' },
      signal
    }).then(r => {
      if (!r.ok) {
        const e = new Error(`HTTP ${r.status} loading community documents`);
        e.status = r.status;
        throw e;
      }
      return r.json();
    });
  };

  // High-level: fetch one page, cancelling any prior request. Used for
  // filter changes and incremental next-page loads.
  const fetchPage = (st, page) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    return fetchOne(st, page, ctrl.signal);
  };

  // Reset + fetch when filters/sort/tab/search change.
  // On initial mount, honor the URL's page (deep-link support); on subsequent
  // changes, reset to page 1.
  //
  // The fetch is debounced 250ms after the last state change so rapid tag
  // toggling or typing in the search box does not hammer the backend. The UI
  // (chips, URL, active-filters row) updates immediately; only the network
  // round-trip waits.
  useEffect(() => {
    const isInitial = isInitialMount.current;
    const isRestore = restoreFromUrlRef.current;
    isInitialMount.current = false;
    restoreFromUrlRef.current = false;
    const page = (isInitial || isRestore) ? effectiveState.page : 1;
    setLoadingDir('initial');
    setPages([]);
    setFirstLoadedPage(page);

    const delay = isInitial || skipDebounceRef.current ? 0 : 250;
    skipDebounceRef.current = false;
    const timer = setTimeout(() => {
      fetchPage(effectiveState, page)
        .then(({ documents, pagination }) => {
          setPages([documents.map(apiDocToCommunityCard)]);
          setPagination(pagination);
          setLoadingDir(null);
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          console.error('community fetch failed:', err);
          setLoadingDir(null);
          onToast && onToast('Failed to load community documents', { type: 'error' });
        });
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveState.tab, effectiveState.q, effectiveState.author, effectiveState.sort,
      // tags is an array — join into a stable string for the dep
      effectiveState.tags.join(',')]);

  // popstate: re-read URL. Back/forward should feel instant, so skip the
  // fetch debounce for this transition.
  useEffect(() => {
    const onPop = () => {
      skipDebounceRef.current = true;
      restoreFromUrlRef.current = true;
      setState(readFromUrl(location.search));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setQuery = (q) => {
    if (q.trim() && !state.q.trim()) window.track && window.track('community_searched');
    const next = { ...state, q, page: 1 };
    setState(next);
    // Typing doesn't pollute history — replace, don't push.
    writeToUrl(next, { mode: 'replace' });
  };

  // IntersectionObserver: when sentinel visible, fetch next page (append).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !pagination?.next || loadingDir !== null) return;
    const obs = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return;
      setLoadingDir('next');
      const next = pagination.next;
      fetchPage(effectiveState, next)
        .then(({ documents, pagination: pg }) => {
          setPages(p => [...p, documents.map(apiDocToCommunityCard)]);
          setPagination(pg);
          setLoadingDir(null);
          const nextState = { ...state, page: next };
          setState(nextState);
          writeToUrl(nextState, { mode: 'replace' });
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          console.error('community next-page fetch failed:', err);
          setLoadingDir(null);
          onToast && onToast('Failed to load more documents', { type: 'error' });
        });
    }, { rootMargin: '600px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [pagination, effectiveState, loadingDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track which page is at the top of the viewport and update the URL.
  useEffect(() => {
    const markers = pageMarkersRef.current;
    if (markers.size < 2) return;
    const visible = new Set();
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const pg = Number(e.target.dataset.page);
        if (e.isIntersecting) visible.add(pg); else visible.delete(pg);
      });
      if (visible.size === 0) return;
      const topPage = Math.min(...visible);
      setState(prev => prev.page === topPage ? prev : { ...prev, page: topPage });
      writeToUrl({ ...state, page: topPage }, { mode: 'replace' });
    }, { rootMargin: '0px 0px -80% 0px' });
    markers.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pages, firstLoadedPage]);

  const setPageMarkerRef = (pageNum) => (el) => {
    if (el) pageMarkersRef.current.set(pageNum, el);
    else pageMarkersRef.current.delete(pageNum);
  };

  // Load all pages before firstLoadedPage in one click, in parallel,
  // so deep-linking into ?page=N doesn't require N-1 manual clicks.
  const handleLoadEarlier = () => {
    if (firstLoadedPage <= 1 || loadingDir !== null) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoadingDir('prev');
    const pagesToLoad = [];
    for (let p = 1; p < firstLoadedPage; p++) pagesToLoad.push(p);
    Promise.allSettled(pagesToLoad.map(p => fetchOne(effectiveState, p, ctrl.signal)))
      .then(settled => {
        const aborted = settled.some(r => r.status === 'rejected' && r.reason?.name === 'AbortError');
        if (aborted) return;

        const failures = [];
        const earlier = settled.map((r, i) => {
          if (r.status === 'fulfilled') return r.value.documents.map(apiDocToCommunityCard);
          failures.push(pagesToLoad[i]);
          return [];
        });

        setPages(p => [...earlier, ...p]);
        setFirstLoadedPage(1);
        setLoadingDir(null);
        const nextState = { ...state, page: 1 };
        setState(nextState);
        writeToUrl(nextState, { mode: 'replace' });

        if (failures.length > 0) {
          const list = failures.length === 1 ? `page ${failures[0]}` : `pages ${failures.join(', ')}`;
          onToast && onToast(`Couldn't load ${list} — retry?`, { type: 'error' });
        }
      });
  };

  const setSort = (sort) => {
    if (sort === state.sort) return;
    window.track && window.track('community_sort_changed', { sort });
    skipDebounceRef.current = true;
    const next = { ...state, sort, page: 1 };
    setState(next);
    writeToUrl(next, { mode: 'push' });
  };

  const setTab = (tab) => {
    if (!isLoggedIn) return;
    if (tab === state.tab) return;
    skipDebounceRef.current = true; // single click — fire immediately
    const next = { ...state, tab, page: 1 };
    setState(next);
    writeToUrl(next, { mode: 'push' });
  };

  const setTags = (tags) => {
    const next = { ...state, tags, page: 1 };
    setState(next);
    writeToUrl(next, { mode: 'push' });
  };

  const toggleFavorite = (doc) => {
    if (pendingFavoriteIds.has(doc.id)) return;
    const wasFavorited = doc.favorited;
    const wasFavoritesCount = doc.favorites;
    const nextFavorited = !wasFavorited;
    setPages(p => p.map(page => page.map(c => c.id === doc.id
      ? { ...c, favorited: nextFavorited, favorites: Math.max(0, (c.favorites || 0) + (nextFavorited ? 1 : -1)) }
      : c)));
    setPendingFavoriteIds(s => { const n = new Set(s); n.add(doc.id); return n; });
    toggleFavoriteOnServer(doc.favoritePath, nextFavorited)
      .then(data => setPages(p => p.map(page => page.map(c => c.id === doc.id
        ? { ...c, favorited: data.favorited, favorites: data.favorites_count }
        : c))))
      .catch(() => {
        setPages(p => p.map(page => page.map(c => c.id === doc.id
          ? { ...c, favorited: wasFavorited, favorites: wasFavoritesCount }
          : c)));
        onToast && onToast('Failed to update favorite', { type: 'error' });
      })
      .finally(() => setPendingFavoriteIds(s => { const n = new Set(s); n.delete(doc.id); return n; }));
  };

  const handleGuestStar = () => {
    onToast && onToast('Sign in or create an account to favorite documents.', { type: 'info' });
  };

  const allCards = pages.flat();
  const bottomLoadedPage = firstLoadedPage + Math.max(pages.length - 1, 0);
  const showLoadEarlier = firstLoadedPage > 1;
  const earlierPageCount = firstLoadedPage - 1;

  const toggleStarred = () => {
    if (!isLoggedIn) return;
    setTab(effectiveState.tab === 'favorites' ? 'explore' : 'favorites');
  };

  return (
    <div className="lib-community-section">
      {isLoggedIn && (
        <div className="lib-section-hd">
          <h2 className="lib-section-title ty-heading-lg">Explore documents made by our community</h2>
        </div>
      )}
      <SmartSearch
        query={state.q}
        onQueryChange={setQuery}
        tags={effectiveState.tags}
        onTagsChange={setTags}
        starred={effectiveState.tab === 'favorites'}
        onStarredToggle={toggleStarred}
        sort={effectiveState.sort}
        onSortChange={setSort}
        isLoggedIn={isLoggedIn}
      />

      {pagination && allCards.length > 0 && (
        <div className="cm-doc-count ty-body-sm">
          {pagination.count} {pagination.count === 1 ? 'document' : 'documents'}
        </div>
      )}

      {showLoadEarlier && (
        <button className="btn btn--secondary btn--md cm-load-earlier"
                onClick={handleLoadEarlier}
                disabled={loadingDir === 'prev'}>
          {loadingDir === 'prev'
            ? `Loading ${earlierPageCount} earlier page${earlierPageCount === 1 ? '' : 's'}…`
            : `↑ Load ${earlierPageCount} earlier page${earlierPageCount === 1 ? '' : 's'}`}
        </button>
      )}

      {loadingDir === 'initial' ? (
        <div className="cm-grid">
          {Array.from({ length: 6 }).map((_, i) => <MyDocCardSkeleton key={`cm-skel-${i}`}/>)}
        </div>
      ) : allCards.length === 0 ? (
        <div className="docs-empty">
          <div className="docs-empty-title">
            {effectiveState.tab === 'favorites' ? 'No favorites yet' : 'No matches'}
          </div>
          <div className="docs-empty-sub">
            {effectiveState.tab === 'favorites'
              ? 'Star a community document to save it here.'
              : 'Try a different search term or clear filters.'}
          </div>
          {effectiveState.tab === 'favorites' && (
            <div className="docs-empty-actions">
              <button className="btn btn--ghost btn--md" onClick={() => setTab('explore')}>
                Explore community
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="cm-grid">
            {pages.map((pageDocs, i) => {
              const pageNum = firstLoadedPage + i;
              return pageDocs.map((d, j) => (
                <React.Fragment key={d.id}>
                  {j === 0 && <div ref={setPageMarkerRef(pageNum)} data-page={pageNum} className="cm-page-marker"/>}
                  <CommunityCard doc={d}
                                 onToggleFavorite={isLoggedIn ? toggleFavorite : undefined}
                                 onGuestStar={isLoggedIn ? undefined : handleGuestStar}
                                 favoritePending={pendingFavoriteIds.has(d.id)}/>
                </React.Fragment>
              ));
            })}
          </div>
          {pagination?.next && (
            <div ref={sentinelRef} className="cm-sentinel">
              {loadingDir === 'next' && (
                <div className="cm-grid">
                  {Array.from({ length: 3 }).map((_, i) => <MyDocCardSkeleton key={`cm-next-${i}`}/>)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

